/**
 * Charm Builder — Analytics
 * Pushes window.dataLayer events for every charm builder interaction, for
 * GTM to pick up. Fully decoupled from the builder's own files:
 *   - Most events are derived by diffing CharmBuilder.state on every
 *     'charm-builder:updated' dispatch (step, material, tabs, base, charms).
 *   - A capture-phase click listener on #charm-builder tags *why* a charm
 *     was removed (stepper / sidebar / canvas) — state alone can't tell.
 *   - Sidebar open is detected via MutationObserver on <dialog open>, since
 *     no state field changes when it opens.
 *   - Add-to-cart click/success/error wraps CharmBuilderCart.submit — it
 *     never throws (errors dispatch 'charm-builder:cart-error' and resolve
 *     normally), so success = "no error event fired during this submit".
 *
 * GTM/GA4 conventions followed throughout (see PR description for the full
 * event/param reference):
 *   - snake_case param names, no undefined values (null instead), prices
 *     always numeric major-currency-units, variant_id always a string.
 *   - { ecommerce: null } is pushed immediately before any push that carries
 *     an `ecommerce` object, per GTM's recommended practice for preventing
 *     data from bleeding between ecommerce events.
 *   - item_id prefers the variant's SKU, falling back to the variant ID.
 *   - item_variant carries the option Shopify calls "Material" on the
 *     variant, falling back to the record's own variant title when a
 *     product has no Material option (rare, but not guaranteed).
 *
 * Loaded last (after charm-builder.js) so CharmBuilder / CharmBuilderCart /
 * __CHARM_BUILDER_DATA__ are already initialised.
 */
(function () {
  const root = document.getElementById("charm-builder");
  if (!root || !window.CharmBuilder) return;

  // GTM may load before or after this script (or not at all, e.g. locally) —
  // initializing up front means every push below queues correctly either way.
  window.dataLayer = window.dataLayer || [];

  const STEP_NAMES = { 1: "select_chain", 2: "select_charms", 3: "review" };
  const CURRENCY = (window.__BUILDER_DATA__ && window.__BUILDER_DATA__.currency) || "USD";

  function push(event, data) {
    window.dataLayer.push(Object.assign({ event: event }, data));
  }

  function toDollars(cents) {
    return Math.round(cents || 0) / 100;
  }

  function clone(state) {
    return JSON.parse(JSON.stringify(state));
  }

  function categoryTitle(list, index) {
    return (list && list[index] && list[index].categoryTitle) || null;
  }

  function totalCharmCount(state) {
    return state.charms.reduce((sum, c) => sum + Math.max(1, c.quantity || 1), 0);
  }

  /** Mirrors CharmBuilder.getDiscountPercent's tier lookup, for a given (non-live) count. */
  function discountForCount(count) {
    return CharmBuilder.getDiscountTiers().reduce((pct, tier) => {
      return count >= tier.threshold && tier.percent > pct ? tier.percent : pct;
    }, 0);
  }

  // ---------------------------------------------------------------------------
  // Variant lookups — __CHARM_BUILDER_DATA__.bases / .charms are arrays of
  // { categoryTitle, products }, each product carrying its full variants
  // array (id, sku, options, ...) straight from the Liquid serialization.
  // Self-contained on purpose: this file never calls into charm-builder.js.
  // ---------------------------------------------------------------------------
  function findVariant(variantId) {
    const data = window.__CHARM_BUILDER_DATA__;
    const pools = [...((data && data.bases) || []), ...((data && data.charms) || [])];
    for (const cat of pools) {
      for (const p of cat.products || []) {
        const v = (p.variants || []).find(v => v.id === variantId);
        if (v) return v;
      }
    }
    return null;
  }

  function itemIdFor(variantId) {
    const variant = findVariant(variantId);
    return (variant && variant.sku) || String(variantId);
  }

  function materialFor(variantId, fallback) {
    const variant = findVariant(variantId);
    return (variant && variant.options && variant.options.Material) || fallback || null;
  }

  // ---------------------------------------------------------------------------
  // Click attribution — capture phase runs before the app's own bubble-phase
  // handlers mutate state, so we can stamp *why* the next diff's removal
  // happened before it happens.
  // ---------------------------------------------------------------------------
  let pendingRemovalSource = null;

  root.addEventListener(
    "click",
    e => {
      if (e.target.closest(".charm-builder__qty-btn--minus")) {
        pendingRemovalSource = "stepper";
      } else if (e.target.closest(".charm-builder__canvas-charm-remove")) {
        pendingRemovalSource = "canvas";
      } else if (e.target.closest("#cb-sidebar-cta")) {
        pendingRemovalSource = "sidebar";
      }
    },
    true
  );

  // ---------------------------------------------------------------------------
  // Wishlist — hearts on Step 2 charm cards are Wishlist King's own
  // <wishlist-button-collection> element (see renderCharmGrid in
  // charm-builder.js). The app handles the toggle itself and exposes no
  // public events, so add vs remove is read from the button's aria-label at
  // click time — capture phase, before the app flips it (and unaffected by
  // the grid's own bubble-phase stopPropagation).
  // ---------------------------------------------------------------------------
  function findCharmProduct(productId) {
    const data = window.__CHARM_BUILDER_DATA__;
    for (const cat of (data && data.charms) || []) {
      const match = (cat.products || []).find(p => p.id === productId);
      if (match) return match;
    }
    return null;
  }

  root.addEventListener(
    "click",
    e => {
      const heart = e.target.closest && e.target.closest(".charm-builder__card-wishlist");
      if (!heart) return;
      const card = heart.closest(".charm-builder__card");
      if (!card) return;

      const productId = parseInt(card.dataset.productId, 10);
      const variantId = parseInt(card.dataset.variantId, 10);
      const product = findCharmProduct(productId);
      const variant = findVariant(variantId);
      // Verified labels: "Add to Wishlist" (not in list) / "In Wishlist"
      // (in list) — so anything without "add" means this click removes.
      const label = (heart.querySelector("button")?.getAttribute("aria-label") || "").toLowerCase();
      const isRemove = label !== "" && label.indexOf("add") === -1;
      const charms = window.__CHARM_BUILDER_DATA__ && window.__CHARM_BUILDER_DATA__.charms;

      push(isRemove ? "charm_builder_wishlist_remove" : "charm_builder_wishlist_add", {
        product_id: productId,
        product_handle: (product && product.handle) || null,
        variant_id: String(variantId),
        item_name: (product && product.title) || null,
        price: toDollars(variant && variant.price),
        item_variant: materialFor(variantId, CharmBuilder.state.activeMetal),
        category_title: categoryTitle(charms, CharmBuilder.state.activeCharmTab),
      });
    },
    true
  );

  // ---------------------------------------------------------------------------
  // Initial view
  // ---------------------------------------------------------------------------
  push("charm_builder_view", {
    builder_title: (window.__BUILDER_DATA__ && window.__BUILDER_DATA__.title) || null,
  });

  // ---------------------------------------------------------------------------
  // State diffing
  // ---------------------------------------------------------------------------
  let prev = clone(CharmBuilder.state);

  document.addEventListener("charm-builder:updated", e => {
    const next = clone(e.detail.state);
    diffStep(prev, next);
    diffMaterial(prev, next);
    diffBaseTab(prev, next);
    diffCharmTab(prev, next);
    diffBase(prev, next);
    diffCharms(prev, next);
    prev = next;
  });

  function diffStep(prevState, nextState) {
    if (prevState.step === nextState.step) return;
    push("charm_builder_step_view", {
      step: nextState.step,
      step_name: STEP_NAMES[nextState.step] || null,
      direction: nextState.step > prevState.step ? "forward" : "back",
    });
  }

  function diffMaterial(prevState, nextState) {
    if (prevState.activeMetal === nextState.activeMetal) return;
    push("charm_builder_material_select", {
      material: nextState.activeMetal || null,
      step: nextState.step,
    });
  }

  function diffBaseTab(prevState, nextState) {
    if (prevState.activeBaseTab === nextState.activeBaseTab) return;
    const bases = window.__CHARM_BUILDER_DATA__ && window.__CHARM_BUILDER_DATA__.bases;
    push("charm_builder_base_tab_select", {
      tab_index: nextState.activeBaseTab,
      category_title: categoryTitle(bases, nextState.activeBaseTab),
    });
  }

  function diffCharmTab(prevState, nextState) {
    if (prevState.activeCharmTab === nextState.activeCharmTab) return;
    const charms = window.__CHARM_BUILDER_DATA__ && window.__CHARM_BUILDER_DATA__.charms;
    push("charm_builder_charm_tab_select", {
      tab_index: nextState.activeCharmTab,
      category_title: categoryTitle(charms, nextState.activeCharmTab),
    });
  }

  function diffBase(prevState, nextState) {
    if (!nextState.base) return;
    if (prevState.base && prevState.base.variantId === nextState.base.variantId) return;
    push("charm_builder_base_select", {
      product_id: nextState.base.productId,
      variant_id: String(nextState.base.variantId),
      item_name: nextState.base.title || null,
      price: toDollars(nextState.base.price),
      item_variant: materialFor(nextState.base.variantId, nextState.activeMetal || nextState.base.variantTitle),
    });
  }

  function diffCharms(prevState, nextState) {
    const prevById = new Map(prevState.charms.map(c => [c.instanceId, c]));
    const nextById = new Map(nextState.charms.map(c => [c.instanceId, c]));

    const added = new Map();
    const removed = new Map();

    nextState.charms.forEach(c => {
      if (!prevById.has(c.instanceId)) bump(added, c);
    });
    prevState.charms.forEach(c => {
      if (!nextById.has(c.instanceId)) bump(removed, c);
    });

    added.forEach(entry => {
      push("charm_builder_charm_add", {
        product_id: entry.productId,
        variant_id: String(entry.variantId),
        item_name: entry.title || null,
        price: toDollars(entry.price),
        quantity: entry.quantity,
        item_variant: materialFor(entry.variantId, nextState.activeMetal || entry.variantTitle),
      });
    });

    if (removed.size) {
      const source = pendingRemovalSource || "unknown";
      removed.forEach(entry => {
        push("charm_builder_charm_remove", {
          product_id: entry.productId,
          variant_id: String(entry.variantId),
          item_name: entry.title || null,
          quantity: entry.quantity,
          item_variant: materialFor(entry.variantId, entry.variantTitle),
          removal_source: source,
        });
      });
    }
    pendingRemovalSource = null;

    diffDiscountTier(prevState, nextState);
    diffCanvasTransform(prevById, nextById);
  }

  function bump(map, charm) {
    const existing = map.get(charm.variantId);
    if (existing) {
      existing.quantity += 1;
    } else {
      map.set(charm.variantId, Object.assign({}, charm, { quantity: 1 }));
    }
  }

  function diffDiscountTier(prevState, nextState) {
    const prevPct = discountForCount(totalCharmCount(prevState));
    const nextPct = discountForCount(totalCharmCount(nextState));
    if (nextPct > prevPct) {
      push("charm_builder_discount_tier_unlocked", {
        tier_pct: nextPct,
        charm_count: totalCharmCount(nextState),
      });
    }
  }

  function diffCanvasTransform(prevById, nextById) {
    nextById.forEach((c, instanceId) => {
      const before = prevById.get(instanceId);
      if (!before) return;
      if (before.x !== c.x || before.y !== c.y) {
        push("charm_builder_canvas_charm_reposition", {
          product_id: c.productId,
          variant_id: String(c.variantId),
          instance_id: instanceId,
          x: c.x,
          y: c.y,
        });
      }
      if (before.rotation !== c.rotation) {
        push("charm_builder_canvas_charm_rotate", {
          product_id: c.productId,
          variant_id: String(c.variantId),
          instance_id: instanceId,
          rotation: c.rotation,
        });
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Sidebar open — no state field flips when the dialog opens, so watch the
  // native <dialog>'s `open` attribute directly.
  // ---------------------------------------------------------------------------
  const sidebarEl = document.getElementById("cb-sidebar");
  if (sidebarEl && window.MutationObserver) {
    let wasOpen = sidebarEl.open;
    new MutationObserver(() => {
      if (sidebarEl.open && !wasOpen) {
        push("charm_builder_sidebar_open", {
          material: CharmBuilder.state.activeMetal || null,
        });
      }
      wasOpen = sidebarEl.open;
    }).observe(sidebarEl, { attributes: true, attributeFilter: ["open"] });
  }

  // ---------------------------------------------------------------------------
  // Add to cart — wrap CharmBuilderCart.submit so click / success / error
  // fire without editing charm-builder-cart.js.
  // ---------------------------------------------------------------------------
  if (window.CharmBuilderCart && typeof window.CharmBuilderCart.submit === "function") {
    const originalSubmit = window.CharmBuilderCart.submit;
    let inFlight = false;
    let failed = false;

    document.addEventListener("charm-builder:cart-error", e => {
      if (!inFlight) return;
      failed = true;
      push("charm_builder_add_to_cart_error", {
        error_message: (e.detail && e.detail.message) || "unknown_error",
      });
    });

    window.CharmBuilderCart.submit = async function () {
      const state = CharmBuilder.state;
      const items = buildEcommerceItems(state);
      const value = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
      const charmCount = totalCharmCount(state);
      const discountPct = CharmBuilder.getDiscountPercent();

      push("charm_builder_add_to_cart_click", {
        charm_count: charmCount,
        discount_pct: discountPct,
        currency: CURRENCY,
        value: value,
      });

      inFlight = true;
      failed = false;
      await originalSubmit.apply(this, arguments);
      inFlight = false;

      if (!failed) {
        // GTM-recommended: clear any prior ecommerce object before pushing a
        // new one, so items/value from an earlier event can't bleed in here.
        window.dataLayer.push({ ecommerce: null });
        push("charm_builder_add_to_cart_success", {
          charm_count: charmCount,
          discount_pct: discountPct,
          ecommerce: {
            currency: CURRENCY,
            value: value,
            items: items,
          },
        });
      }
    };
  }

  function buildEcommerceItems(state) {
    const items = [];
    if (state.base) {
      items.push({
        item_id: itemIdFor(state.base.variantId),
        item_name: state.base.title || null,
        item_variant: materialFor(state.base.variantId, state.base.variantTitle),
        price: toDollars(state.base.price),
        quantity: 1,
      });
    }

    const byVariant = new Map();
    state.charms.forEach(c => {
      const existing = byVariant.get(c.variantId);
      if (existing) {
        existing.quantity += c.quantity || 1;
      } else {
        byVariant.set(c.variantId, {
          item_id: itemIdFor(c.variantId),
          item_name: c.title || null,
          item_variant: materialFor(c.variantId, c.variantTitle),
          price: toDollars(c.price),
          quantity: c.quantity || 1,
        });
      }
    });
    byVariant.forEach(v => items.push(v));

    return items;
  }
})();
